import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Provider } from '@/features/game-context/types';
import { getErrorType } from '@/features/game-window-integration/helpers/error-mapping';
import { processDgcError } from '@/features/game-window-integration/providers/dgc/error-helpers';
import {
  DgcReceivingEvents,
  DgcSendingEvents,
} from '@/features/game-window-integration/providers/dgc/types';
import { setErrorCorrelationId } from '@/features/game-window-integration/store/game-window-helper-data';
import { ProviderLoggerMessages } from '@/features/game-window-integration/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';

export const useDgcHandlers = () => {
  const operatorOrigin = window.location.origin;
  const dispatch = useDispatch();
  const getGameProviderGameClient = useCallback(
    () =>
      document.querySelector<HTMLIFrameElement>('#game-client')?.contentWindow,
    [],
  );

  const sendInitMessage = useCallback(
    (event) => {
      const { source, origin } = event ?? {};
      dispatch(setErrorCorrelationId(origin));
      const message = {
        method: DgcSendingEvents.OPERATOR_READY,
        data: {
          origin: operatorOrigin,
        },
      };
      source?.postMessage(message, origin as WindowPostMessageOptions);
    },
    [dispatch, operatorOrigin],
  );

  const handleGameInitialization = useCallback(
    (event) => {
      sendInitMessage(event);
    },
    [sendInitMessage],
  );

  const handleReturnToLobby = useCallback(() => {
    WrapperProxy.setGameLauncherClosed();
  }, []);

  const handleGameReady = useCallback(() => {
    setTimeout(() => {
      WrapperProxy.setGameLoaded();
    }, 1000);
  }, []);

  const handleSpinStarted = useCallback(() => {
    WrapperProxy.setSpinInProgress(true);
  }, []);

  const handleSpinEnd = useCallback(() => {
    WrapperProxy.setSpinInProgress(false);
  }, []);

  const handleVolumeChange = useCallback((event) => {
    WrapperProxy.setAudio(event?.data?.method === DgcReceivingEvents.SOUND_ON);
  }, []);

  const handleNotImplemented = useCallback(() => {
    Logger.rumAction({
      name: ProviderLoggerMessages.NOT_IMPLEMENTED,
      useCase: DdEventCategory.ProviderIntegration,
      eventType: DdEventType.Info,
      context: {
        provider: Provider.DGC,
      },
    });
  }, []);

  const handleError = useCallback((payload) => {
    const payloadData = payload?.data?.data;
    const { errorType, errorCode, errorMessage } = processDgcError(payloadData);
    WrapperProxy.setProviderError({
      display: true,
      errorCode,
      errorMessage,
      errorType: getErrorType(errorType, Provider.DGC),
    });
  }, []);

  return {
    handleGameInitialization,
    handleGameReady,
    handleSpinStarted,
    handleSpinEnd,
    handleVolumeChange,
    handleNotImplemented,
    handleError,
    getGameProviderGameClient,
    handleReturnToLobby,
  };
};
